/**
 * Customers API service using centralized API instance
 * Uses api/v1 prefix for customers endpoints
 */
import api from './api'
import { saveBlob } from '../../lib/download'
import type {
  DetailedCustomer,
  CustomerCreateParams,
  CustomerUpdateParams,
  CustomerListResponse,
  CustomerResponse,
  CustomerFilters,
  CustomerStats
} from '@crm-src/types/customer'

export const customersApi = {
  /**
   * Get customers with optional filters and pagination
   */
  getAll: async (filters?: CustomerFilters): Promise<CustomerListResponse> => {
    const params = new URLSearchParams()

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
    }

    const response = await api.get(`/customers?${params.toString()}`)

    // Handle ApiResponse.paginated format
    return {
      customers: response.data.data || [],
      stats: response.data.stats,
      pagination: response.data.meta?.pagination || {}
    }
  },

  /**
   * Get a single customer by ID
   */
  getById: async (id: string): Promise<CustomerResponse> => {
    const response = await api.get(`/customers/${id}`)
    return {
      customer: response.data.data || response.data.customer
    }
  },

  /**
   * Create a new customer
   */
  create: async (customerData: CustomerCreateParams): Promise<CustomerResponse> => {
    const response = await api.post('/customers', { customer: customerData })
    return {
      customer: response.data.data || response.data.customer,
      message: response.data.message
    }
  },

  /**
   * Update an existing customer
   */
  update: async (id: string, customerData: CustomerUpdateParams): Promise<CustomerResponse> => {
    const response = await api.put(`/customers/${id}`, { customer: customerData })
    return {
      customer: response.data.data || response.data.customer,
      message: response.data.message
    }
  },

  /**
   * Delete a customer (soft delete)
   */
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/customers/${id}`)
    return {
      message: response.data.message || 'Customer deleted successfully'
    }
  },

  /**
   * Bulk delete customers
   */
  bulkDelete: async (customerIds: string[]): Promise<{
    message: string,
    deleted_count: number,
    failed_deletions?: Array<{
      id: string,
      name: string,
      company?: string,
      errors: string[]
    }>
  }> => {
    const response = await api.delete('/customers/bulk_delete', {
      data: { customer_ids: customerIds }
    })

    return {
      message: response.data.message || 'Customers processed successfully',
      deleted_count: response.data.data?.deleted_count || response.data.deleted_count || 0,
      failed_deletions: response.data.data?.failed_deletions || response.data.failed_deletions || []
    }
  },

  /**
   * Bulk assign salespeople to customers
   */
  bulkAssignSalespeople: async (customerIds: string[], salespeopleIds: string[]): Promise<{
    message: string,
    assigned_count: number,
    total_assignments: number,
    failed_assignments?: Array<{
      id: string,
      name: string,
      error: string
    }>
  }> => {
    const response = await api.post('/customers/bulk_assign_salespeople', {
      customer_ids: customerIds,
      salesperson_ids: salespeopleIds
    })
    return response.data
  },

  /**
   * Bulk assign reseller to customers
   */
  bulkAssignReseller: async (customerIds: string[], resellerId: string): Promise<{
    message: string,
    assigned_count: number,
    failed_assignments?: Array<{
      id: string,
      name: string,
      error: string
    }>
  }> => {
    const response = await api.post('/customers/bulk_assign_reseller', {
      customer_ids: customerIds,
      reseller_id: resellerId
    })
    return response.data
  },

  /**
   * Get customer statistics
   */
  getStats: async (): Promise<CustomerStats> => {
    const response = await api.get('/customers/stats')
    return response.data.data || response.data
  },

  /**
   * Assign a salesperson to a customer
   */
  assignSalesperson: async (customerId: string, userId: string, role: string = 'salesperson', notes?: string): Promise<{
    message: string;
    customer: any;
  }> => {
    const response = await api.post(`/customers/${customerId}/assign_salesperson`, {
      user_id: userId,
      role,
      notes
    })
    return response.data
  },

  /**
   * Remove a salesperson from a customer
   */
  removeSalesperson: async (customerId: string, userId: string): Promise<{
    message: string;
    customer: any;
  }> => {
    const response = await api.delete(`/customers/${customerId}/remove_salesperson`, {
      data: { user_id: userId }
    })
    return response.data
  },

  /**
   * Get salespeople for a customer
   */
  getSalespeople: async (customerId: string): Promise<{
    customer_id: string;
    customer_name: string;
    salespeople: any[];
  }> => {
    const response = await api.get(`/customers/${customerId}/salespeople`)
    return response.data
  },

  /**
   * Convert customer back to prospect
   */
  convertToProspect: async (customerId: string, options?: { force_delete_agreements?: boolean }): Promise<{
    message: string;
    prospect: { id: string; full_name: string; email: string; status: string };
    customer: { id: string; status: string };
  }> => {
    const response = await api.post(`/customers/${customerId}/convert_to_prospect`, options)
    return response.data
  },

  /**
   * Import customers from JSON data
   */
  import: async (customers: any[]): Promise<{
    imported: number;
    total: number;
    errors: string[];
    success_rate: number;
  }> => {
    const response = await api.post('/customers/import', { customers })
    return response.data
  },

  /**
   * Download export file with proper filename
   */
  downloadExport: async (format: 'csv' | 'xlsx' = 'csv', filters?: CustomerFilters, filename?: string): Promise<void> => {
    const params = new URLSearchParams()

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
    }

    params.append('format', format)

    const response = await api.get(`/customers/export?${params.toString()}`, {
      responseType: 'blob'
    })

    const blob = response.data
    const defaultFilename = `customer_export_${new Date().toISOString().split('T')[0]}.${format}`

    // Native OS Save-As dialog in the desktop app; browser download on the web.
    await saveBlob(filename || defaultFilename, blob)
  }
}

// Export default for convenience
export default customersApi
